import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useLocation } from "react-router-dom";
import { BookOpen, Search, ChevronLeft, ChevronRight } from "lucide-react";
import { useAdminLogbooksQuery, useAdminDietSummaryQuery } from "../api/adminLogbookApi";
import {
  LB_STATUS_TABS,
  LbStatusKey,
  lbStatusColors,
  LbStatusBadge,
  LbCountPill,
  fmtDate,
  memberName,
} from "../components/adminLogbookUi";
import { CreateLogbookButton, RowStatusEditor } from "../components/adminLogbookActions";
import AccessGate from "../../../components/AccessGate";
import { PERMS } from "../../../utils/perms";

const PER_PAGE = 50;

function SummaryCard({ label, value, tone }: { label: string; value: number; tone: string }) {
  const c = lbStatusColors(tone);
  return (
    <div className={`border rounded-xl p-3 ${c.pill}`}>
      <div className="text-2xl font-bold">{value}</div>
      <div className="text-xs font-medium opacity-80">{label}</div>
    </div>
  );
}

// Map a tab key to its count in the server summary payload.
function tabCount(summary: any, key: LbStatusKey): number {
  if (!summary) return 0;
  if (key === "all") return Number(summary.total ?? 0);
  return Number(summary[key] ?? 0);
}

export default function AdminDietLogbooksPage() {
  const { dietId = "" } = useParams();
  const location = useLocation();
  const dietTitle = (location.state as any)?.dietTitle as string | undefined;

  const [statusTab, setStatusTab] = useState<LbStatusKey>("all");
  const [q, setQ] = useState("");
  const [debouncedQ, setDebouncedQ] = useState("");
  const [page, setPage] = useState(1);

  // Debounce the search box so we don't hit the API on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedQ(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  // Reset to page 1 whenever the filter changes.
  useEffect(() => {
    setPage(1);
  }, [statusTab, debouncedQ]);

  // Accurate counts for the whole diet (server-side, all rows).
  const { data: summary, isLoading: summaryLoading } = useAdminDietSummaryQuery(dietId, { skip: !dietId });

  // The rows for the current tab/page (server-paginated).
  const { data, isLoading, isFetching, isError, refetch } = useAdminLogbooksQuery({
    logbook_diet_id: dietId,
    status: statusTab === "all" ? undefined : statusTab,
    search: debouncedQ || undefined,
    page,
    per_page: PER_PAGE,
  });

  const rows: any[] = useMemo(() => (Array.isArray(data?.data) ? data.data : []), [data]);
  const currentPage = Number(data?.current_page ?? page);
  const lastPage = Number(data?.last_page ?? 1);
  const total = Number(data?.total ?? rows.length);

  const title = dietTitle ?? rows[0]?.diet?.title ?? "Diet";

  return (
    <div className="max-w-6xl mx-auto p-4 md:p-6">
      <Link to="/admin/logbooks" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-800 mb-3">
        <ChevronLeft className="w-4 h-4" /> All diets
      </Link>

      <div className="bg-white border rounded-xl p-6">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-50 border border-indigo-100">
              <BookOpen className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-gray-800">Logbooks — {title}</h1>
              <p className="text-sm text-gray-500 mt-0.5">
                {summaryLoading ? "Loading…" : `${tabCount(summary, "all")} logbooks in this diet.`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <AccessGate perm={PERMS.logbook_diet.create}>
              <CreateLogbookButton dietId={dietId} dietTitle={title} />
            </AccessGate>
            <button onClick={() => refetch()} disabled={isFetching} className="px-4 py-2 rounded-lg border hover:bg-gray-50 text-sm">
              {isFetching ? "Refreshing…" : "Refresh"}
            </button>
          </div>
        </div>

        {/* Summary cards (server counts) */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mt-5">
          {LB_STATUS_TABS.map((t) => (
            <SummaryCard key={t.key} label={t.label} value={tabCount(summary, t.key)} tone={t.key} />
          ))}
        </div>

        {/* Search */}
        <div className="mt-5 relative max-w-md">
          <Search className="absolute left-3 top-2.5 w-4 h-4 text-gray-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search by member name or email…"
            className="w-full pl-9 pr-3 py-2 border rounded-lg text-sm outline-none focus:ring-2 focus:ring-gray-100"
          />
        </div>

        {/* Status tabs (server counts) */}
        <div className="flex flex-wrap gap-2 mt-4">
          {LB_STATUS_TABS.map((t) => {
            const active = statusTab === t.key;
            const c = lbStatusColors(t.key);
            return (
              <button
                key={t.key}
                onClick={() => setStatusTab(t.key)}
                className={["px-3 py-2 text-sm border rounded-md transition", active ? c.active : `${c.border} ${c.inactive}`].join(" ")}
              >
                {t.label}
                <LbCountPill tone={t.key} count={tabCount(summary, t.key)} />
              </button>
            );
          })}
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="mt-6 text-sm text-gray-500">Loading logbooks…</div>
        ) : isError ? (
          <div className="mt-6 text-sm text-red-600">Failed to load logbooks.</div>
        ) : rows.length === 0 ? (
          <div className="mt-6 p-4 rounded-md border bg-gray-50 text-sm text-gray-700">No logbooks match your filters.</div>
        ) : (
          <>
            <div className="mt-5 border rounded-xl overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-gray-500 border-b">
                    <th className="px-4 py-2 font-medium">Member</th>
                    <th className="px-4 py-2 font-medium">Stage</th>
                    <th className="px-4 py-2 font-medium">Supervisor</th>
                    <th className="px-4 py-2 font-medium">Progress</th>
                    <th className="px-4 py-2 font-medium">Status</th>
                    <th className="px-4 py-2 font-medium">Started</th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map((r) => (
                    <tr key={r.id} className="hover:bg-gray-50/60">
                      <td className="px-4 py-3">
                        <div className="font-medium text-gray-900">{r.user?.name ?? "—"}</div>
                        <div className="text-xs text-gray-400">{r.user?.member?.membership_no ?? r.user?.email ?? ""}</div>
                      </td>
                      <td className="px-4 py-3 text-gray-700">Stage {r.stage ?? "—"}</td>
                      <td className="px-4 py-3 text-gray-700">{memberName(r.supervisor)}</td>
                      <td className="px-4 py-3 text-gray-500">
                        {r.position != null && r.size != null ? `${r.position} / ${r.size}` : "—"}
                      </td>
                      <td className="px-4 py-3">
                        <AccessGate perm={PERMS.logbook_diet.activate} fallback={<LbStatusBadge status={r.status} />}>
                          <RowStatusEditor logbookId={r.id} status={r.status} />
                        </AccessGate>
                      </td>
                      <td className="px-4 py-3 text-gray-500">{fmtDate(r.created_at)}</td>
                      <td className="px-4 py-3 text-right">
                        <Link to={`/admin/logbooks/${r.id}`} className="px-3 py-1.5 rounded-lg border text-xs hover:bg-gray-50">
                          Review
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="mt-4 flex items-center justify-between text-sm text-gray-600">
              <span>
                Page {currentPage} of {lastPage} · {total} total
              </span>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage <= 1 || isFetching}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border hover:bg-gray-50 disabled:opacity-40"
                >
                  <ChevronLeft className="w-4 h-4" /> Prev
                </button>
                <button
                  onClick={() => setPage((p) => Math.min(lastPage, p + 1))}
                  disabled={currentPage >= lastPage || isFetching}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border hover:bg-gray-50 disabled:opacity-40"
                >
                  Next <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
